import { useEffect, useRef, useState } from 'react';
import { initials } from '../../lib/account';
import { chatPrefs } from '../../lib/chat';
import { wearingHere } from '../../lib/operator';
import { isSeed } from '@engineer-os/chat';
import { sound } from '../../lib/sound';
import { Button, LinkButton, Machine } from '../../ui';
import { useStrings } from '../../state/useLocale';
import { Operator } from '../avatar/Operator';
import type { AccountApi } from '../../state/useAccount';

/**
 * The corner of the level list that knows who you are.
 *
 * Signed out, it is one quiet button. Signed in, it is the person's picture
 * and first name, and a small panel behind them with the email the account
 * is under, one line saying what the account does, and the way out. Nothing
 * else lives here; the account has no settings.
 *
 * What is on the button is what a chat room shows for this person: their
 * operator if they have built one, otherwise the picture on their Google
 * account, otherwise the figure their id gives them (lib/operator.ts). A
 * figure is drawn here in the tab and cannot fail; a picture is Google's and
 * cross-origin, and this page is cross-origin isolated, so a plain img works
 * because Google serves the header that allows it — and a picture that fails
 * to load falls back to initials, as it always did.
 *
 * The email is the one thing in here the person did not write: it is an
 * address, Latin and full of the punctuation bidi reorders, so it is
 * isolated. The name is not — a name is prose, and forcing it left to right
 * would put an Arabic one's words in the wrong order.
 */
export function Account({ account }: { account: AccountApi }) {
  const s = useStrings().standing.account;
  const profile = useStrings().profile;
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!account.enabled) return null;

  if (!account.user) {
    return (
      <Button
        variant="quiet"
        disabled={account.busy}
        title={s.signInWhy}
        onClick={() => {
          sound.click();
          account.signIn();
        }}
      >
        {account.busy ? s.signingIn : s.signIn}
      </Button>
    );
  }

  const { user } = account;
  const firstName = user.name.trim().split(/\s+/)[0] ?? user.name;
  const wearing = wearingHere(user.id, chatPrefs().face, user.picture);
  const avatar = isSeed(wearing) ? (
    <Operator seed={wearing} size={22} className="account-avatar" />
  ) : wearing === user.picture && !broken ? (
    <img className="account-avatar" src={wearing} alt="" width={22} height={22} referrerPolicy="no-referrer" onError={() => setBroken(true)} />
  ) : wearing === user.picture ? (
    <span className="account-avatar account-initials" aria-hidden="true">
      {initials(user.name)}
    </span>
  ) : (
    /* An emoji face, picked in the chat. It is text, so it is drawn as text. */
    <span className="account-avatar account-face emoji" aria-hidden="true">
      {wearing}
    </span>
  );

  return (
    <div className="account" ref={rootRef}>
      <button
        type="button"
        className="account-btn"
        aria-expanded={open}
        aria-controls="account-panel"
        title={s.signedInAs(user.name)}
        onClick={() => setOpen((o) => !o)}
      >
        {avatar}
        <span className="account-name">{firstName}</span>
      </button>
      {open && (
        <div id="account-panel" className="account-pop" role="dialog" aria-label={s.menu}>
          <strong className="account-full">{user.name}</strong>
          {user.email && <Machine className="note">{user.email}</Machine>}
          <span className="note">{s.what}</span>
          {/* The way to the record and to the builder. A link, because it is a
              screen with a URL and not a thing this menu does. */}
          <LinkButton variant="quiet" href="?profile=me">
            {profile.open}
          </LinkButton>
          <Button
            variant="quiet"
            disabled={account.busy}
            onClick={() => {
              setOpen(false);
              void account.signOut();
            }}
          >
            {s.signOut}
          </Button>
        </div>
      )}
    </div>
  );
}

export default Account;
